import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from '@app/store';
import {
  fetchCategoryDocumentById,
  createCategoryDocument,
  updateCategoryDocument,
  CategoryDocumentActions,
} from "./CategoryDocumentSlice";
import { fetchAffiliates } from "../affiliate/AffiliateSlice";
import { fetchUserProfile } from "../auth/authSlice";
import { CategoryDocumentMapper } from "./CategoryDocumentMapper";
import { CategoryDocumentOutputDTO } from './types';
import { AffiliateOutputDTO } from "../affiliate/types";
import { DataForm } from '@components/ui';
import { Field } from '@components/ui/DataForm';
import { FormSkeleton } from '@components/ui/FormSkeleton';
import { ApiError } from '@/types/api';
import { useNotification } from '@hooks/useNotification';
import { Box, Typography, Alert } from "@mui/material";
import { useAuth } from '@hooks/useAuth';
import { UserRole } from "../user";

const CategoryDocumentForm: React.FC = (props:any) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentItem, loading } = useSelector(
    (state: RootState) => state.categorydocument
  );
  const { items: affiliates, loading: affiliatesLoading } = useSelector(
    (state: RootState) => state.affiliate
  );
  // Get current categorydocument info from auth state
  const { user, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState<Partial<CategoryDocumentOutputDTO>>({});
  const [formError, setFormError] = useState<ApiError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const notification = useNotification();

  const isLoadingRef = useRef(false);
  const initialLoadDoneRef = useRef(false);
  const submitAttemptRef = useRef(false);

  // Make sure we have the current categorydocument data
  useEffect(() => {
    if (isAuthenticated && !user) {
      console.log("🔄 Fetching current categorydocument profile");
      dispatch(fetchUserProfile());
    }
  }, [dispatch, isAuthenticated, user]);

  // Debug output for current categorydocument
  useEffect(() => {
    console.log("🧪 Current authenticated categorydocument:", user);
  }, [user]);

  // CategoryDocument categorydocument-based access control
  const currentCategoryDocumentCategoryDocument = user?.role as UserRole;
  
  // Redirect lawyer categorydocuments away from this form
  useEffect(() => {
    if (currentCategoryDocumentCategoryDocument === UserRole.LAWYER) {
      notification.showError("Você não tem permissão para acessar esta página");
      navigate("/");
    }
  }, [currentCategoryDocumentCategoryDocument, navigate, notification]);

  useEffect(() => {
    const loadData = async () => {
      if (isLoadingRef.current || initialLoadDoneRef.current) {
        return;
      }

      try {
        console.log("📡 Iniciando carregamento do Usuário:", id);
        isLoadingRef.current = true;

        // Load affiliates
        console.log("📡 CategoryDocumentForm - Carregando Afiliados");
        await dispatch(fetchAffiliates({}));

        if (id && props.onClose==undefined) {
          console.log("📡 CategoryDocumentForm - Carregando Usuário:", id);
          const resultAction = await dispatch(fetchCategoryDocumentById(id));

          if (fetchCategoryDocumentById.fulfilled.match(resultAction)) {
            console.log("✅ Usuário carregado com sucesso");
          } else {
            throw new Error("Erro ao carregar dados");
          }
        }
      } catch (error) {
        console.error("❌ Erro ao carregar Usuário:", error);
        notification.showError("Erro ao carregar dados");
        setFormError({
          message: "Erro ao carregar dados",
          code: "LOAD_ERROR",
          statusCode: 500,
        });
      } finally {
        isLoadingRef.current = false;
        initialLoadDoneRef.current = true;
        setIsInitialLoad(false);
      }
    };

    loadData();

    return () => {
      console.log("🧹 Limpando estado do formulário");
      dispatch(CategoryDocumentActions.clearCurrentItem());
    };
  }, [dispatch, id, notification]);

  useEffect(() => {
    if (currentItem) {
      console.log("📝 CategoryDocument - Atualizando dados do formulário:", currentItem);
      setFormData({
        name: currentItem.name,
        type: currentItem.type,
        enterpriseId: currentItem.enterpriseId,
      });
    }
  }, [currentItem]);

  // Filter available categorydocument options based on current categorydocument's categorydocument
  

  // Determine if enterprise_id field should be disabled
  const isEnterpriseIdDisabled = () => {
    // Only ADMIN can modify enterprise_id
    return currentCategoryDocumentCategoryDocument !== UserRole.ADMIN && !!formData.enterpriseId;
  };

  const fields: Field[] = [
    {
      name: "name",
      label: "Nome",
      type: "text",
      required: true,
      validationError: formError?.validationErrors?.name?.message,
    },
    {
      name: "type",
      label: "Tipo de Categoria",
      type: "select",
      required: true,
      validationError: formError?.validationErrors?.type?.message,
      options: [{_id:'document',name:'Para documento'},{_id:'template',name:'Para template'},{_id:'techinical',name:'Para peça técnica'}].map((affiliate: any) => ({
        value: affiliate._id || "",
        label: affiliate.name || "Sem nome",
      })),
    },
   
    {
      name: "enterpriseId",
      label: "Empresa",
      type: "select",
      required: true,
      disabled: isEnterpriseIdDisabled(),
      options: affiliates.map((affiliate: AffiliateOutputDTO) => ({
        value: affiliate._id || "",
        label: affiliate.name || "Sem nome",
      })),
      validationError: formError?.validationErrors?.enterpriseId?.message,
    },
  ];

  const handleSubmit = async (data: Partial<CategoryDocumentOutputDTO>) => {
    if (submitAttemptRef.current) {
      notification.showWarning("Submissão em andamento, aguarde...");
      return;
    }

    try {
      submitAttemptRef.current = true;
      setIsSubmitting(true);
      setFormError(null);
      console.log(currentCategoryDocumentCategoryDocument)
      // If password is empty in edit mode, remove it from the payload
      
      // If the categorydocument is a Manager trying to create an Admin, prevent it

      // If the categorydocument is a Manager, keep the existing enterpriseId or set the current categorydocument's enterpriseId
      // if (currentCategoryDocumentCategoryDocument === UserRole.ADMIN || currentCategoryDocumentCategoryDocument === UserRole.SUPER || currentCategoryDocumentCategoryDocument === UserRole.MANAGER) {
      //   if (id && formData.enterpriseId) {
      //     // Keep the original enterprise ID when editing
      //     data.enterpriseId = formData.enterpriseId;
      //   } else if (!id) {
      //     // Set the manager's own enterprise ID for new categorydocuments
      //     data.enterpriseId = categorydocument?.enterpriseId;
      //   }
      // }

      const mappedData = CategoryDocumentMapper.toDTO(data);
      console.log("📤 Enviando dados:", mappedData);

      const resultAction = (id && props.onClose==undefined)
        ? await dispatch(updateCategoryDocument({ id, data: mappedData }))
        : await dispatch(createCategoryDocument(mappedData));

      if (
        updateCategoryDocument.rejected.match(resultAction) ||
        createCategoryDocument.rejected.match(resultAction)
      ) {
        const error = resultAction.payload as ApiError;
        setFormError(error);
        notification.showError(error.message || "Erro ao salvar dados");
        return;
      }

      notification.showSuccess("Dados salvos com sucesso!");
      if(props.onClose){
        props.onClose();
      }
      else{
      navigate("/categories-documents");
      }
    } catch (error) {
      console.error("❌ CategoryDocumentForm - Erro inesperado:", error);
      notification.showError("Erro inesperado ao salvar os dados");
      setFormError({
        code: "UNKNOWN_ERROR",
        message: "Erro inesperado ao salvar os dados",
        statusCode: 500,
      });
    } finally {
      setIsSubmitting(false);
      submitAttemptRef.current = false;
    }
  };

  // Return nothing if categorydocument is a lawyer (will be redirected)
  if (currentCategoryDocumentCategoryDocument === UserRole.LAWYER) {
    return null;
  }

  if (isInitialLoad && (loading || affiliatesLoading)) {
    return <FormSkeleton fields={6} />;
  }

  return (
    <Box sx={{ p: 0 }}>
      {/* <Typography variant="h5" gutterBottom>
        {id ? "Editar Função" : "Nova Função"}
      </Typography> */}
      
      {currentCategoryDocumentCategoryDocument === UserRole.MANAGER && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Como gerente, você não pode criar usuários administradores nem alterar a empresa vinculada ao usuário.
        </Alert>
      )}
      
      <DataForm
        data={formData}
        fields={fields}
        onClose={props.onClose}
        isEdit={id!=undefined}

        onSubmit={handleSubmit}
        basePath="categories-documents"
        title={id ? "Editar Categoria" : "Nova Categoria"}
        error={formError}
        onClearError={() => setFormError(null)}
        disabled={isSubmitting}
      />
    </Box>
  );
};

export default CategoryDocumentForm;
